import type { ForfeitureRiskData, HealthBarBand } from '../../api/health-bar'
import { RHYTHM_STYLE } from '../../lib/rhythm-style'

// ── ForfeitureBanner ─────────────────────────────────────────────────────────
//
// Full-width "book your annual before it lapses" banner shown on the home,
// below the wellbeing + balances row. Relocated out of the HealthBarCard so
// the wellbeing card stays about *rhythm*, and the booking nudge lives on the
// action surface. Styled after the redesign prototype: icon + headline + body
// with a solid CTA button on the right.
//
// Severity → colour: `soft` (amber, plan ahead) / `firm` (red, will lapse).
// `none` renders nothing.

/** Policy v1.0 §1.2 forfeiture copy — no "forfeit" jargon in the headline. */
function forfeitureCopy(
  risk: ForfeitureRiskData,
): { headline: string; body: string; ctaLabel: string } {
  const days = risk.daysAtRisk
  const dayWord = days === 1 ? 'day' : 'days'
  const months = risk.monthsToYearEnd
  const mFrag =
    months === 0
      ? 'before year-end'
      : `${months} month${months === 1 ? '' : 's'} to year-end`
  if (risk.severity === 'firm') {
    return {
      headline: 'Some annual leave is about to lapse.',
      body: `${days} ${dayWord} of annual will lapse on 31 Dec, eh — ${mFrag}.`,
      ctaLabel: 'Book annual now',
    }
  }
  return {
    headline: 'Plan your remaining annual leave.',
    body: `You have ${days} ${dayWord} of annual still unbooked, ${mFrag}. Plan ahead so it doesn't lapse, can?`,
    ctaLabel: 'Plan annual leave',
  }
}

export function ForfeitureBanner({
  risk,
  band,
}: {
  risk: ForfeitureRiskData
  /** Rhythm band of the card above — themes the banner to match it. */
  band: HealthBarBand
}): JSX.Element | null {
  if (risk.severity === 'none') return null
  const { headline, body, ctaLabel } = forfeitureCopy(risk)

  // Colour follows the leave-rhythm band (green/amber/red), so the banner
  // reads as part of the same surface as the wellbeing card above it. The
  // forfeiture *severity* only drives the wording, not the colour.
  const r = RHYTHM_STYLE[band]
  const bg = r.fill
  const line = r.chipBg
  const fg = r.chipText

  return (
    <div
      data-testid="home-forfeiture-banner"
      data-severity={risk.severity}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '14px 18px',
        borderRadius: 12,
        border: `1px solid ${line}`,
        background: bg,
      }}
    >
      <div aria-hidden="true" style={{ fontSize: 26, flexShrink: 0, lineHeight: 1 }}>
        ⏳
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: fg, marginBottom: 3 }}>
          {headline}
        </div>
        <div style={{ fontSize: 12.5, color: fg, lineHeight: 1.5 }}>{body}</div>
      </div>
      <a
        href="/apply?leaveType=annual"
        data-testid="home-forfeiture-cta"
        style={{
          flexShrink: 0,
          padding: '8px 16px',
          background: fg,
          color: 'var(--surface)',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {ctaLabel} →
      </a>
    </div>
  )
}
